import { useToast } from "vue-toastification";
import { Capacitor } from "@capacitor/core";
// import { useCapacitorPushNotifications, getLastDeviceId, getLastPushToken } from "@/composables/usePushNotifications";
import { useCapacitorPushNotifications } from "@/composables/usePushNotifications";
// import apiClient from "@/services/axios";
import { subscribeToMercure, unsubscribeFromMercure } from "@/services/mercure";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";



let instance: ReturnType<typeof createMessaging> | null = null;

function createMessaging() {
  const toast = useToast();
  const { t } = useI18n();
  const router = useRouter();

  let mercureUnsubscribe: (() => void) | null = null;
  let currentUserIdentifier: string | null = null;
  let isSubscribing = false;
  let announcedChanges: string[] = [];

  const registerHandlers = async (user: any) => {
    if (!user?.userIdentifier) return;

    // fix to prevent duplicate subscriptions for same user
    if (currentUserIdentifier === user.userIdentifier && (mercureUnsubscribe || isSubscribing)) {
      return;
    }

    // Mark synchronously (before the first await) so concurrent calls — e.g.
    // App mount and the router guard firing at the same time — don't each open
    // their own Mercure connection.
    isSubscribing = true;
    currentUserIdentifier = user.userIdentifier;


    //Native Push notifications only
    if (Capacitor.getPlatform() !== 'web') {
    
      await useCapacitorPushNotifications();
    }


    // Web Mercure subscription  
    if (mercureUnsubscribe) {
      mercureUnsubscribe();
      mercureUnsubscribe = null;
    }

    const eventHandler = (payload: any) => {
      if (payload?.event) {
        handleTenantMembershipEvent(payload);
        return;
      }

      if (!payload?.data || payload.data.type !== "device_status_change") return;

      const data = payload.data;
      // An Organization Admin who owns the device receives the same change on his own and on
      // the admin channel; it is announced once. The timestamp has whole seconds only, so the
      // change itself is part of the key.
      const changeKey = `${data.deviceId}|${data.timestamp}|${JSON.stringify(data.changes ?? null)}`;
      if (announcedChanges.includes(changeKey)) return;
      announcedChanges = [...announcedChanges.slice(-49), changeKey];
      const previousStatus = data.changes?.status?.old;
      const newStatus = data.changes?.status?.new;
      const deviceName = data.deviceName;
      // we needed to capitalize the device type "lobas" -> "Lobas"
      const deviceType = typeof data.deviceType === "string" && data.deviceType.length ? data.deviceType[0].toUpperCase() + data.deviceType.slice(1) : "";
      const translationKey = getStatusChangeKey(previousStatus, newStatus);
       console.log("Translation Key:", translationKey);
      if (!translationKey) return;
       toast.info(
        t(translationKey, {
          deviceType,
          deviceName,
        })
      );

      window.dispatchEvent(
        new CustomEvent("devices:refresh", {
          detail: { page: 1 }
        })
      );
    };

    const handleTenantMembershipEvent = async (payload: any) => {
      const event = payload.event as string;
      const tenantName = payload.tenantName ?? '';
      const role = payload.role ?? '';

      const keyMap: Record<string, string> = {
        tenant_membership_added: 'notifications.tenant.membership_added',
        tenant_membership_removed: 'notifications.tenant.membership_removed',
        tenant_membership_role_changed: 'notifications.tenant.role_changed',
        tenant_ownership_transferred: 'notifications.tenant.ownership_transferred',
        tenant_renamed: 'notifications.tenant.renamed',
        tenant_device_access_removed: 'notifications.tenant.device_access_removed',
      };

      const key = keyMap[event];
      if (!key) return;

      const count = Array.isArray(payload.deviceIds) ? payload.deviceIds.length : 0;
      toast.info(t(key, { tenantName, role, count }));

      window.dispatchEvent(new CustomEvent('tenant-membership:change', { detail: payload }));

      // UV13: take over the new state in place instead of signing out.
      if (!(await useAuthStore().refreshUser())) {
        router.push('/');
        return;
      }

      window.dispatchEvent(new CustomEvent('devices:refresh', { detail: { page: 1 } }));

      // UV14: run the current view through the guards again; a view that is
      // no longer allowed ends on the notice. Without force, vue-router skips
      // a navigation to the location it is already on.
      router.replace({ ...router.currentRoute.value, force: true });
    };

    try {
      mercureUnsubscribe = await subscribeToMercure(user.userIdentifier, eventHandler, undefined, {
        seesAllDevices: useAuthStore().isOrgAdmin,
      });
    } catch (err) {
      console.error("Mercure subscription failed:", err);
    } finally {
      isSubscribing = false;
    }
  };

  const cleanupHandlers = () => {
    currentUserIdentifier = null;
    isSubscribing = false;

    if (mercureUnsubscribe) {
      mercureUnsubscribe();
      mercureUnsubscribe = null;
    }

    unsubscribeFromMercure();
  };

  function getStatusChangeKey(
    oldStatus?: string,
    newStatus?: string
  ): string | null {
    if (!newStatus) return null;

    const transitionMap: Record<string, string> = {
      "pending->connected": "notifications.status.pending_connected",
      "connected->disconnected": "notifications.status.connected_disconnected",
      "disconnected->connected": "notifications.status.disconnected_connected",
      "connected->connection_lost": "notifications.status.connected_connection_lost",
      "connection_lost->connected": "notifications.status.connection_lost_connected",
      "update_required->connected": "notifications.status.update_required_connected",
      "update_required->disconnected": "notifications.status.update_required_disconnected",
      "connection_lost->disconnected": "notifications.status.connection_lost_disconnected",
    };

    if (oldStatus) {
      const transitionKey = transitionMap[`${oldStatus}->${newStatus}`];
      if (transitionKey) return transitionKey;
    }

    const newStatusMap: Record<string, string> = {
      pending: "notifications.status.pending",
      update_required: "notifications.status.update_required",
    };

    return newStatusMap[newStatus] ?? "notifications.status.fallback";
  }

  return {
    registerHandlers,
    cleanupHandlers,
  };
}

//Public composable (singleton), very important to avoid multiple Mercure subscriptions
export function useMessaging() {
  if (!instance) {
    instance = createMessaging();
  }
  return instance;
}
